import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';

const Pre = chakra('pre', {
  base: {
    margin: 0,
    fontFamily: 'mono',
    bg: 'surface.subtle',
    color: 'text.primary',
    borderWidth: '1px',
    borderColor: 'border.subtle',
    borderRadius: 'md',
    overflowX: 'auto',
    whiteSpace: 'pre',
    lineHeight: 'normal',
  },
});

const InlineCode = chakra('code', {
  base: {
    fontFamily: 'mono',
    bg: 'surface.subtle',
    color: 'text.primary',
    borderWidth: '1px',
    borderColor: 'border.subtle',
    borderRadius: 'sm',
    px: '1.5',
    py: '0.5',
    fontSize: '0.9em',
    whiteSpace: 'nowrap',
  },
});

type CodeSize = 'sm' | 'md';

const sizeStyle: Record<CodeSize, HTMLChakraProps<'pre'>> = {
  sm: { fontSize: 'xs', p: '2.5' },
  md: { fontSize: 'sm', p: '3.5' },
};

export interface CodeBlockProps extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  /** Raw string content. Mutually exclusive with `value`. */
  children?: ReactNode;
  /** A value to pretty-print as JSON (objects, arrays, ids). Overrides `children`. */
  value?: unknown;
  /** Render inline (a `<code>` chip) instead of a block. */
  inline?: boolean;
  size?: CodeSize;
}

/**
 * Monospace code / JSON viewer for ids, tokens, and tool payloads (§10, §14). A block
 * scrolls horizontally inside its own container so wide content never scrolls the page
 * body; `inline` renders a compact code chip for ids in running text. Replaces the
 * legacy MUI `JsonBlock`.
 */
export const CodeBlock = forwardRef<HTMLElement, CodeBlockProps>(function CodeBlock(
  { children, value, inline, size = 'md', ...rest },
  ref,
) {
  const content = value !== undefined ? JSON.stringify(value, null, 2) : children;
  if (inline) {
    return (
      <InlineCode ref={ref as React.Ref<HTMLElement>} {...rest}>
        {content}
      </InlineCode>
    );
  }
  return (
    <Pre ref={ref as React.Ref<HTMLPreElement>} {...sizeStyle[size]} {...rest}>
      {content}
    </Pre>
  );
});
